import { makeAutoObservable } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import States from '../states';
import { Track } from 'react-native-idc-player';
import { DetailedMovieItem } from '../../data/models/cinema';
import { SeasonEpisodeType } from '../../types/others';

export default function usePlayerState(
  content?: DetailedMovieItem,
  season?: number,
  episode?: number,
) {
  const store = useLocalObservable(
    () => new PlayerState(content, season, episode),
  );
  return store;
}

export class PlayerState {
  _position = 0;
  _duration = 0;
  _content?: DetailedMovieItem = undefined;
  _seasonEpisode?: SeasonEpisodeType = undefined;

  _playing = false;
  _buffering = false;

  _delta = 0;
  _hoverPosition?: number = undefined;
  _seekStep = 10;

  _favorite = false;
  _archiveAvailable = false;

  _quality = -1;
  _audio = -1;
  _subtitles = -1;
  _tracks: Track[] = [];

  audioChanged = false;
  subtitlesChanged = false;

  constructor(content?: DetailedMovieItem, season?: number, episode?: number) {
    makeAutoObservable(this, {
      _seekStep: false,
      _archiveAvailable: false,
      audioChanged: false,
      subtitlesChanged: false,
    });
    this._content = content;
    this._seasonEpisode = {
      season: season ?? 0,
      episode: episode ?? 0,
    };
  }

  get position(): number {
    return this._position;
  }
  set position(value: number) {
    this._position = value;
  }

  get duration(): number {
    return this._duration;
  }
  set duration(value: number) {
    this._duration = value;
  }

  get content(): DetailedMovieItem | undefined {
    return this._content;
  }
  set content(value: DetailedMovieItem | undefined) {
    this._content = value;
  }

  get seasonEpisode() {
    return this._seasonEpisode;
  }
  set seasonEpisode(value: SeasonEpisodeType | undefined) {
    this._seasonEpisode = value;
  }

  get playing(): boolean {
    return this._playing;
  }
  set playing(value: boolean) {
    this._playing = value;
  }

  get buffering(): boolean {
    return this._buffering;
  }
  set buffering(value: boolean) {
    this._buffering = value;
  }

  get delta(): number {
    return this._delta;
  }
  stepDelta(multipler: number) {
    const d = this._delta + this._seekStep * multipler;
    if (this.content === undefined) {
      if (!this._archiveAvailable) return;

      const archive = States.mainState.archiveParams;
      const buffer = Math.ceil(States.mainState.bufferLength / 1000);
      const isLive = this._duration === -1;

      const time = isLive ? this._position : States.timeState.currentTime;
      const edge = time - archive.length;
      const result = this._position + d;

      if (!isFinite(this._delta)) {
        if (multipler < 0) this._delta = time - archive.delay - this._position;
      } else if (result > time - archive.delay - buffer) {
        if (this._delta !== 0) {
          if (isLive) this._delta = 0;
          else this._delta = Infinity;
        } else if (this._delta === 0) {
          if (multipler < 0) this._delta = -archive.delay - buffer;
          else if (!isLive) this._delta = Infinity;
        }
      } else if (result < edge) {
        this._delta = time - archive.length;
      } else {
        this._delta = d - (d % this._seekStep);
      }
      return;
    }
    this._delta = Math.min(
      this._duration - this._position,
      Math.max(-this._position, d - (d % this._seekStep)),
    );
  }
  resetDelta() {
    this._delta = 0;
  }

  get hoverPosition() {
    return this._hoverPosition;
  }
  set hoverPosition(value: number | undefined) {
    this._hoverPosition = value;
  }

  get favorite() {
    return this._favorite;
  }
  set favorite(value: boolean) {
    this._favorite = value;
  }

  get quality() {
    return this._quality;
  }
  set quality(value: number) {
    this._quality = value;
  }

  get audio() {
    return this._audio;
  }
  set audio(value: number) {
    this._audio = value;
  }

  get subtitles() {
    return this._subtitles;
  }
  set subtitles(value: number) {
    this._subtitles = value;
  }

  get tracks() {
    return this._tracks;
  }
  set tracks(value: Track[]) {
    this._tracks = value;
  }
}
